import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import RedoIcon from '@mui/icons-material/Redo';
import BlockIcon from '@mui/icons-material/Block';
import { type ApolloError } from '@apollo/client';
import { Check } from '@mui/icons-material';

import { green, yellow } from 'utils/color-styles';
import Img from 'retired/Img';
import CloseSquareIcon from 'assets/icons/close-square.svg';
import SadCloud from 'assets/sad-cloud.avif';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancelDonation?: () => void;
  onConfirm: () => void;
  onMarkAsResolved?: () => void;
  isLoading: boolean;
  error?: ApolloError;
}

export const RetryModal = ({
  isOpen,
  onClose,
  onConfirm,
  onMarkAsResolved,
  onCancelDonation,
  isLoading,
  error,
}: ModalProps): JSX.Element => (
  <Dialog open={isOpen} onClose={onClose}>
    <DialogContent style={{ padding: '18px 18px 32px' }}>
      <Stack spacing={2}>
        <Box
          style={{
            backgroundColor: yellow,
            textAlign: 'center',
            position: 'relative',
            borderRadius: 20,
          }}
        >
          <IconButton style={{ position: 'absolute', right: 16, top: 16 }} onClick={onClose}>
            <Img
              src={CloseSquareIcon}
              alt="Close"
              style={{
                width: 26,
                height: 26,
              }}
            />
          </IconButton>

          <Img src={SadCloud} alt="Unsuccesfull Payroll Donation" style={{ width: 190 }} />
        </Box>
        <Typography
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          Unresolved Payroll Donation
        </Typography>
        <Typography style={{ textAlign: 'center' }}>
          Manually resolve the status of this payroll donation. We recommend you confirm this status
          in your payroll system before proceeding to update the status.{' '}
          {onCancelDonation && (
            <>
              Marking as Unsuccessful will result in the employee being notified their deduction was
              unsuccessful.{' '}
            </>
          )}
          Would you like to rerun the donation in the next cycle?
        </Typography>
        {error && (
          <Alert severity="error">
            An error occurred while updating the unresolved donation. Please try again later.
          </Alert>
        )}
        <Box style={{ display: 'flex', justifyContent: 'space-around' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={onCancelDonation || onClose}
            disabled={isLoading}
            endIcon={isLoading ? undefined : <BlockIcon />}
          >
            No, {onCancelDonation ? 'mark as Unsuccessful' : "don't retry this donation"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={isLoading}
            endIcon={isLoading ? undefined : <RedoIcon />}
          >
            {isLoading ? 'Rescheduling donation…' : 'Yes, rerun next cycle'}
          </Button>
        </Box>
        {onMarkAsResolved ? (
          <Box style={{ display: 'flex', justifyContent: 'space-around' }}>
            <Button
              variant="contained"
              color="success"
              sx={{ border: `1px solid ${green}` }}
              onClick={onMarkAsResolved}
              disabled={isLoading}
              endIcon={isLoading ? undefined : <Check />}
            >
              {isLoading ? 'Marking as successful' : 'Mark as successful'}
            </Button>
          </Box>
        ) : null}
      </Stack>
    </DialogContent>
  </Dialog>
);
